import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  CloudUploadOutlined,
  DownloadOutlined,
  ErrorOutline,
  FactCheckOutlined,
  PlaylistAddCheckOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import axios from "axios";
import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuid } from "uuid";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { CommonDrawerStyle, DrawerHeader } from "../Common/styles/drawer";
import ScreenerImportFailedRowsTable from "./ScreenerImportFailedRowsTable";
import {
  extractFailedRows,
  ScreenerImportChunk,
} from "./screenerImportRowResults";

const MAX_FILE_SIZE = 200 * 1024 * 1024;

type UploadStatus = "idle" | "uploading" | "uploaded" | "failed";

type ImportStep =
  | "upload"
  | "mapping"
  | "validating"
  | "preview_errors"
  | "preview_success"
  | "confirming"
  | "polling"
  | "done"
  | "failed";

type CanonicalQuestion = {
  id: string;
  label: string;
  varname?: string;
  type?: string;
  choices?: Array<{ label?: string }>;
};

type Mapping = {
  header: string;
  canonicalQuestionId: string | null;
  profileAttribute: string | null;
  isEmail?: boolean;
};

type PreviewError = {
  row: number;
  email?: string;
  field?: string;
  message: string;
};

type PreviewData = {
  totalRows: number;
  validCount: number;
  errorCount: number;
  errors: PreviewError[];
  preview: Record<string, unknown>[];
};

type JobData = {
  id?: string;
  status?: string;
  phase?: string | null;
  totalRows?: number;
  processedRows?: number;
  successCount?: number;
  failedCount?: number;
  errorSummary?: string | null;
  resultUrl?: string | null;
  url?: string | null;
  chunks?: ScreenerImportChunk[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
};

const PROFILE_ATTRIBUTES = [
  "firstName",
  "lastName",
  "gender",
  "dob",
  "phoneNumber",
  "occupation",
  "line1",
  "line2",
  "address",
  "city",
  "state",
  "country",
  "zipcode",
] as const;

const REQUIRED_GENDER_CHOICE_LABELS = [
  "Male",
  "Female",
  "Other",
  "Prefer not to say",
];
const CHOICE_TYPES = new Set(["checkbox", "radio", "dropdown"]);

const genderChoicesAreValid = (
  choices: Array<{ label?: string }> | undefined,
) =>
  Array.isArray(choices) &&
  choices.length === 4 &&
  choices.every((choice) =>
    REQUIRED_GENDER_CHOICE_LABELS.includes(String(choice?.label ?? "")),
  );

const STEP_LABELS = ["Upload", "Map columns", "Validate", "Import"];

const MAPPING_PREVIEW_STATUS = [
  "Reading your CSV…",
  "Detecting column headers…",
  "Matching columns to profile fields and questions…",
  "Preparing the mapping table…",
];

const VALIDATE_STATUS = [
  "Checking emails and duplicates…",
  "Validating phone, ZIP, and date of birth…",
  "Checking mapped answers and choices…",
  "Looking up emails already in the registry…",
];

const CONFIRM_STATUS = [
  "Creating the import form…",
  "Preparing participant records…",
  "Queuing chunks for processing…",
  "Starting the import job…",
];

const ProcessLoader = ({
  title,
  messages,
  caption,
  icon,
}: {
  title: string;
  messages: string[];
  caption: string;
  icon: ReactNode;
}) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % messages.length);
    }, 2200);
    return () => window.clearInterval(timer);
  }, [messages.length]);

  return (
    <Stack alignItems="center" spacing={2.5} sx={{ py: 10, px: 2 }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          bgcolor: "rgba(103, 80, 164, 0.08)",
          display: "grid",
          placeItems: "center",
          position: "relative",
          animation: "mappingPulse 1.8s ease-in-out infinite",
          "@keyframes mappingPulse": {
            "0%, 100%": { transform: "scale(1)", opacity: 0.8 },
            "50%": { transform: "scale(1.06)", opacity: 1 },
          },
        }}
      >
        <CircularProgress
          size={72}
          thickness={2.4}
          sx={{ position: "absolute" }}
        />
        {icon}
      </Box>
      <Box textAlign="center">
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, minHeight: 22 }}
        >
          {messages[index]}
        </Typography>
      </Box>
      <Box sx={{ width: "100%", maxWidth: 360 }}>
        <LinearProgress sx={{ height: 6, borderRadius: 6 }} />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        textAlign="center"
        sx={{ maxWidth: 380 }}
      >
        {caption}
      </Typography>
    </Stack>
  );
};

const unwrap = (response: any): any => {
  let value = response?.data ?? response;
  while (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length <= 3 &&
    "data" in value
  ) {
    value = value.data;
  }
  return value;
};

const toNumber = (value: unknown): number => Number(value ?? 0) || 0;

const isEmailLike = (mapping: Mapping) =>
  mapping.isEmail ||
  ["email", "email_address", "emailaddress"].includes(
    mapping.header
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_"),
  );

const ScreenerImportDrawer = ({ open, onClose, onImportComplete }: Props) => {
  const [step, setStep] = useState<ImportStep>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [gcsPath, setGcsPath] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const uploadGenerationRef = useRef(0);
  const [fileInputKey, setFileInputKey] = useState(uuid());
  const [mappingLoading, setMappingLoading] = useState(false);
  const [canonicalLoading, setCanonicalLoading] = useState(false);
  const [canonicalOptions, setCanonicalOptions] = useState<CanonicalQuestion[]>(
    [],
  );
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<JobData | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);

  const activeStage =
    mappingLoading || step === "mapping"
      ? 1
      : step === "upload"
        ? 0
        : ["validating", "preview_errors", "preview_success"].includes(step)
          ? 2
          : 3;

  const duplicateProfileAttributes = useMemo(() => {
    const selected = mappings
      .map((mapping) => mapping.profileAttribute)
      .filter(Boolean) as string[];
    return Array.from(
      new Set(
        selected.filter((value, index) => selected.indexOf(value) !== index),
      ),
    );
  }, [mappings]);

  const hasEmailMapping = mappings.some(isEmailLike);

  const genderChoiceMappingErrors = useMemo(() => {
    return mappings.flatMap((mapping) => {
      if (mapping.profileAttribute !== "gender") return [];
      const canonical = mapping.canonicalQuestionId
        ? canonicalOptions.find(
            (option) => option.id === mapping.canonicalQuestionId,
          )
        : undefined;
      if (!canonical || !CHOICE_TYPES.has(canonical.type || "")) return [];
      if (!canonical.choices?.length) return [];
      if (genderChoicesAreValid(canonical.choices)) return [];
      return [
        `“${mapping.header}” is mapped to gender. The linked canonical must use exactly Male, Female, Other, and Prefer not to say.`,
      ];
    });
  }, [canonicalOptions, mappings]);

  const reset = useCallback(() => {
    uploadGenerationRef.current += 1;
    setStep("upload");
    setFile(null);
    setGcsPath(null);
    setUploadStatus("idle");
    setFileInputKey(uuid());
    setMappings([]);
    setPreview(null);
    setJobId(null);
    setJob(null);
    setResultUrl(null);
    setMappingLoading(false);
  }, []);

  const uploadFileToGcs = useCallback(async (selected: File) => {
    const generation = ++uploadGenerationRef.current;
    setUploadStatus("uploading");
    setGcsPath(null);
    try {
      const urlRes = await http.post(
        "/admin/registry/participants/screener-import/upload-url",
        { fileName: selected.name },
      );
      const payload = unwrap(urlRes) ?? {};
      const uploadUrl = payload.uploadUrl ?? payload.presignedUploadUrl;
      const path = payload.gcsPath ?? payload.postUploadImageUrl;
      if (!uploadUrl || !path) {
        throw new Error("Could not obtain a signed upload URL.");
      }
      await axios.put(uploadUrl, selected, {
        headers: { "Content-Type": selected.type || "text/csv" },
      });
      if (generation !== uploadGenerationRef.current) return;
      setGcsPath(String(path));
      setUploadStatus("uploaded");
    } catch (err) {
      if (generation !== uploadGenerationRef.current) return;
      setGcsPath(null);
      setUploadStatus("failed");
      errorToastMessage(err as Error);
    }
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    setCanonicalLoading(true);
    http
      .get("/admin/registry/canonical-questions?size=100")
      .then((res) => {
        const payload = unwrap(res);
        const rows = Array.isArray(payload)
          ? payload
          : (payload?.data ?? payload?.items ?? payload?.results ?? []);
        setCanonicalOptions(
          rows.map((item: any) => ({
            id: String(item.id),
            label: item.label || item.varname || item.name || String(item.id),
            varname: item.varname,
            type: item.type,
            choices: item.choices,
          })),
        );
      })
      .catch((err) => errorToastMessage(err as Error))
      .finally(() => setCanonicalLoading(false));
  }, [open]);

  const loadResultUrl = useCallback(async (id: string) => {
    try {
      const res = await http.get(
        `/admin/registry/participants/screener-import/jobs/${id}/result`,
      );
      const data = unwrap(res);
      setResultUrl(data?.url ?? data?.downloadUrl ?? data?.resultUrl ?? null);
    } catch {
      setResultUrl(null);
    }
  }, []);

  useEffect(() => {
    if (step !== "polling" || !jobId) return;
    let active = true;
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await http.get(
          `/admin/registry/participants/screener-import/jobs/${jobId}`,
        );
        if (!active) return;
        const data: JobData = unwrap(res);
        setJob(data);
        const status = String(data?.status || "").toLowerCase();
        if (
          ["completed", "completed_with_errors", "done", "success"].includes(
            status,
          )
        ) {
          setStep("done");
          onImportComplete();
          await loadResultUrl(jobId);
        } else if (["failed", "cancelled", "rolled_back"].includes(status)) {
          setStep("failed");
        }
      } catch (err) {
        if (active) {
          errorToastMessage(err as Error);
          setStep("failed");
        }
      } finally {
        inFlight = false;
      }
    };

    void poll();
    const timer = window.setInterval(poll, 2500);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [jobId, loadResultUrl, onImportComplete, step]);

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith(".csv")) {
      toastMessage("warning", "Please select a CSV file.");
      setFileInputKey(uuid());
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      toastMessage("warning", "File size must be 200MB or smaller.");
      setFileInputKey(uuid());
      return;
    }
    setFile(selected);
    setGcsPath(null);
    void uploadFileToGcs(selected);
  };

  const loadMappingPreview = async () => {
    if (!file || !gcsPath) {
      toastMessage("warning", "Wait for the file to finish uploading.");
      return;
    }
    setMappingLoading(true);
    try {
      const res = await http.post(
        "/admin/registry/participants/screener-import/mapping-preview",
        { gcsPath },
      );
      const data = unwrap(res) ?? {};
      const headers: string[] = data.headers ?? [];
      const autoMappings: any[] = Array.isArray(data.mappings)
        ? data.mappings
        : [];
      const normalized = headers.map((header, index) => {
        const automatic =
          autoMappings.find((item) => item.header === header) ??
          autoMappings[index] ??
          {};
        return {
          header,
          canonicalQuestionId:
            automatic.canonicalQuestionId ?? automatic.canonical?.id ?? null,
          profileAttribute: automatic.profileAttribute ?? null,
          isEmail: automatic.isEmail ?? false,
        };
      });
      setMappings(normalized);
      if (Array.isArray(data.canonicals) && data.canonicals.length) {
        setCanonicalOptions(
          data.canonicals.map((item: any) => ({
            id: String(item.id),
            label: item.label || item.varname || String(item.id),
            varname: item.varname,
            type: item.type,
            choices: item.choices,
          })),
        );
      }
      setStep("mapping");
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setMappingLoading(false);
    }
  };

  const updateMapping = (index: number, patch: Partial<Mapping>) => {
    setMappings((current) =>
      current.map((mapping, mappingIndex) =>
        mappingIndex === index ? { ...mapping, ...patch } : mapping,
      ),
    );
  };

  const mappingsJson = () =>
    JSON.stringify(
      mappings.map(
        ({ header, canonicalQuestionId, profileAttribute, isEmail }) => ({
          header,
          canonicalQuestionId,
          profileAttribute,
          isEmail,
        }),
      ),
    );

  const validate = async () => {
    if (!file || duplicateProfileAttributes.length) return;
    if (!gcsPath) {
      toastMessage("warning", "Wait for the file to finish uploading.");
      return;
    }
    setStep("validating");
    try {
      const res = await http.post(
        "/admin/registry/participants/screener-import/preview",
        { mappings: mappingsJson(), gcsPath },
      );
      const data = unwrap(res) ?? {};
      const normalized: PreviewData = {
        totalRows: toNumber(data.totalRows ?? data.total),
        validCount: toNumber(data.validCount ?? data.valid),
        errorCount: toNumber(
          data.errorCount ?? data.invalidCount ?? data.errors?.length,
        ),
        errors: Array.isArray(data.errors) ? data.errors : [],
        preview: Array.isArray(data.preview)
          ? data.preview
          : Array.isArray(data.sampleRows)
            ? data.sampleRows
            : [],
      };
      setPreview(normalized);
      setStep(normalized.errorCount > 0 ? "preview_errors" : "preview_success");
    } catch (err) {
      errorToastMessage(err as Error);
      setStep("mapping");
    }
  };

  const confirmImport = async () => {
    if (!file || !preview || preview.errorCount > 0) return;
    if (!gcsPath) {
      toastMessage("warning", "Wait for the file to finish uploading.");
      return;
    }
    setStep("confirming");
    try {
      const res = await http.post(
        "/admin/registry/participants/screener-import/confirm",
        { mappings: mappingsJson(), gcsPath },
      );
      const data = unwrap(res);
      const id = data?.jobId ?? data?.id;
      if (!id) throw new Error("Import started without a job ID.");
      setJobId(String(id));
      setJob({
        id: String(id),
        status: "running",
        phase: "starting",
        totalRows: preview.totalRows,
        processedRows: 0,
        successCount: 0,
        failedCount: 0,
      });
      setStep("polling");
    } catch (err) {
      errorToastMessage(err as Error);
      setStep("preview_success");
    }
  };

  const restart = () => {
    reset();
    setFileInputKey(uuid());
  };

  const progress = job?.totalRows
    ? Math.min(
        100,
        (toNumber(job.processedRows) / toNumber(job.totalRows)) * 100,
      )
    : 0;

  const phaseLabel = String(job?.phase || "Preparing import")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const failedRows = useMemo(
    () => extractFailedRows(job?.chunks),
    [job?.chunks],
  );
  const hasImportErrors =
    toNumber(job?.failedCount) > 0 || failedRows.length > 0;

  const sampleHeaders = preview?.preview?.length
    ? Array.from(new Set(preview.preview.flatMap((row) => Object.keys(row))))
    : [];

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box
        sx={{
          ...CommonDrawerStyle,
          width: { xs: "100vw", sm: "760px" },
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <DrawerHeader title="Mapped Import" closeDrawer={handleClose} />

        <Stack
          direction="row"
          alignItems="center"
          sx={{ mb: 3, flexShrink: 0 }}
        >
          {STEP_LABELS.map((label, index) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                alignItems: "center",
                flex: index === 3 ? 0 : 1,
              }}
            >
              <Stack alignItems="center" spacing={0.5}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    typography: "caption",
                    fontWeight: 700,
                    color:
                      index <= activeStage
                        ? "primary.contrastText"
                        : "text.secondary",
                    bgcolor:
                      index <= activeStage ? "primary.main" : "action.hover",
                  }}
                >
                  {index < activeStage ? "✓" : index + 1}
                </Box>
                <Typography
                  variant="caption"
                  color={
                    index <= activeStage ? "text.primary" : "text.secondary"
                  }
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {label}
                </Typography>
              </Stack>
              {index < 3 && <Divider sx={{ flex: 1, mx: 1.5, mt: -2 }} />}
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: ["mapping", "preview_errors", "preview_success"].includes(
              step,
            )
              ? "hidden"
              : "auto",
          }}
        >
          {step === "upload" && mappingLoading && (
            <ProcessLoader
              title="Preparing column mappings"
              messages={MAPPING_PREVIEW_STATUS}
              caption="We’re scanning the file headers and suggesting matches. This can take a moment on larger CSVs."
              icon={
                <CloudUploadOutlined color="primary" sx={{ fontSize: 28 }} />
              }
            />
          )}

          {step === "upload" && !mappingLoading && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Upload participant data
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Upload a CSV, then map its columns to participant details and
                  canonical questions.
                </Typography>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 5,
                  borderStyle: "dashed",
                  borderWidth: 2,
                  textAlign: "center",
                  bgcolor: "action.hover",
                }}
              >
                <CloudUploadOutlined
                  color="primary"
                  sx={{ fontSize: 44, mb: 1 }}
                />
                <Typography fontWeight={600}>
                  {file?.name || "Choose a CSV file"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={2}
                >
                  CSV only, up to 200MB
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={uploadStatus === "uploading"}
                >
                  {file ? "Change file" : "Browse file"}
                  <input
                    key={fileInputKey}
                    hidden
                    type="file"
                    accept=".csv,text/csv"
                    disabled={uploadStatus === "uploading"}
                    onChange={handleFileSelect}
                  />
                </Button>
                {uploadStatus === "uploading" && (
                  <Box sx={{ mt: 2.5, mx: "auto", maxWidth: 320 }}>
                    <LinearProgress sx={{ height: 6, borderRadius: 6 }} />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Uploading file…
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Upload in progress - wait before choosing a different file
                    </Typography>
                  </Box>
                )}
                {uploadStatus === "uploaded" && (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mt: 2 }}
                  >
                    <CheckCircleOutline color="success" fontSize="small" />
                    <Typography
                      variant="body2"
                      color="success.main"
                      fontWeight={600}
                    >
                      Upload complete
                    </Typography>
                  </Stack>
                )}
                {uploadStatus === "failed" && (
                  <Stack spacing={1.25} alignItems="center" sx={{ mt: 2 }}>
                    <Alert
                      severity="error"
                      sx={{ textAlign: "left", width: "100%" }}
                    >
                      Upload failed. Check your connection and try again.
                    </Alert>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => file && void uploadFileToGcs(file)}
                    >
                      Retry upload
                    </Button>
                  </Stack>
                )}
              </Paper>
              <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                <Button variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={
                    !file ||
                    !gcsPath ||
                    uploadStatus !== "uploaded" ||
                    mappingLoading
                  }
                  onClick={loadMappingPreview}
                >
                  Review mappings
                </Button>
              </Stack>
            </Stack>
          )}

          {step === "mapping" && (
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0, height: "100%" }}>
              <Box sx={{ flexShrink: 0 }}>
                <Typography variant="h6" fontWeight={600}>
                  Map CSV columns
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A column can map to a canonical question, a profile attribute,
                  or both. Clear a field to leave it unmapped.
                </Typography>
              </Box>
              {!hasEmailMapping && (
                <Alert severity="warning" sx={{ flexShrink: 0 }}>
                  An email-like column is required to identify participants.
                </Alert>
              )}
              {!!duplicateProfileAttributes.length && (
                <Alert severity="warning" sx={{ flexShrink: 0 }}>
                  Profile attributes can only be used once. Duplicates:{" "}
                  <strong>{duplicateProfileAttributes.join(", ")}</strong>
                </Alert>
              )}
              {genderChoiceMappingErrors.map((message) => (
                <Alert key={message} severity="warning" sx={{ flexShrink: 0 }}>
                  {message}
                </Alert>
              ))}
              <Paper
                variant="outlined"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "26%" }}>
                          Sheet header
                        </TableCell>
                        <TableCell sx={{ width: "43%" }}>Canonical</TableCell>
                        <TableCell sx={{ width: "31%" }}>
                          Profile attribute
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mappings.map((mapping, index) => {
                        const selectedCanonical =
                          canonicalOptions.find(
                            (option) =>
                              option.id === mapping.canonicalQuestionId,
                          ) ?? null;
                        return (
                          <TableRow
                            key={`${mapping.header}-${index}`}
                            sx={
                              isEmailLike(mapping)
                                ? { bgcolor: "rgba(46, 125, 50, 0.05)" }
                                : undefined
                            }
                          >
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography variant="body2" fontWeight={500}>
                                  {mapping.header}
                                </Typography>
                                {isEmailLike(mapping) && (
                                  <Chip
                                    label="Email"
                                    size="small"
                                    color="success"
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={canonicalOptions}
                                loading={canonicalLoading}
                                value={selectedCanonical}
                                getOptionLabel={(option) =>
                                  option.varname
                                    ? `${option.label} (${option.varname})`
                                    : option.label
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                onChange={(_, value) =>
                                  updateMapping(index, {
                                    canonicalQuestionId: value?.id ?? null,
                                  })
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Not mapped"
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={mapping.profileAttribute ?? ""}
                                  displayEmpty
                                  onChange={(event) =>
                                    updateMapping(index, {
                                      profileAttribute:
                                        String(event.target.value) || null,
                                    })
                                  }
                                >
                                  <MenuItem value="">None</MenuItem>
                                  {PROFILE_ATTRIBUTES.map((attribute) => (
                                    <MenuItem key={attribute} value={attribute}>
                                      {attribute}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ flexShrink: 0, pt: 0.5 }}
              >
                <Button variant="outlined" onClick={restart}>
                  Choose another file
                </Button>
                <Button
                  variant="contained"
                  onClick={validate}
                  disabled={
                    !hasEmailMapping ||
                    !!duplicateProfileAttributes.length ||
                    !!genderChoiceMappingErrors.length
                  }
                >
                  Validate data
                </Button>
              </Stack>
            </Stack>
          )}

          {step === "validating" && (
            <ProcessLoader
              title="Validating mapped data"
              messages={VALIDATE_STATUS}
              caption="We’re checking every row against the same rules used at import. Large files can take a little while."
              icon={<FactCheckOutlined color="primary" sx={{ fontSize: 28 }} />}
            />
          )}

          {(step === "preview_errors" || step === "preview_success") &&
            preview && (
              <Stack spacing={2} sx={{ flex: 1, minHeight: 0, height: "100%" }}>
                <Alert
                  severity={preview.errorCount ? "error" : "success"}
                  sx={{ flexShrink: 0 }}
                >
                  {preview.errorCount
                    ? "Resolve the validation errors in your CSV before importing."
                    : "Validation complete. The file is ready to import."}
                </Alert>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: "wrap", flexShrink: 0 }}
                >
                  <Chip
                    label={`${preview.totalRows} total`}
                    variant="outlined"
                  />
                  <Chip label={`${preview.validCount} valid`} color="success" />
                  <Chip
                    label={`${preview.errorCount} errors`}
                    color={preview.errorCount ? "error" : "default"}
                  />
                </Stack>
                <Box
                  sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 1, pb: 1 }}
                >
                  {!!preview.errors.length && (
                    <Paper
                      variant="outlined"
                      sx={{ overflow: "hidden", mb: 2 }}
                    >
                      <Box sx={{ height: 280, overflow: "auto", pr: 0.5 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Row</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Field</TableCell>
                              <TableCell>Issue</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {preview.errors.map((error, index) => (
                              <TableRow key={`${error.row}-${index}`}>
                                <TableCell>{error.row}</TableCell>
                                <TableCell>{error.email || "—"}</TableCell>
                                <TableCell>{error.field || "—"}</TableCell>
                                <TableCell>{error.message}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                      {preview.errorCount > preview.errors.length && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", px: 1.5, py: 1 }}
                        >
                          Showing the first {preview.errors.length} issues of{" "}
                          {preview.errorCount} rows with errors. Fix these and
                          re-validate to see more.
                        </Typography>
                      )}
                    </Paper>
                  )}
                  {!!preview.preview.length && (
                    <Box>
                      <Typography variant="subtitle2">
                        Preview of valid rows
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={1}
                      >
                        First few rows that passed validation (not the error
                        list). Scroll sideways to see every column.
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ overflowX: "auto", maxHeight: 220 }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              {sampleHeaders.map((header) => (
                                <TableCell
                                  key={header}
                                  sx={{ whiteSpace: "nowrap" }}
                                >
                                  {header}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {preview.preview
                              .slice(0, 10)
                              .map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                  {sampleHeaders.map((header) => {
                                    const value =
                                      row[header] == null
                                        ? "—"
                                        : String(row[header]);
                                    return (
                                      <TableCell
                                        key={header}
                                        sx={{
                                          maxWidth: 180,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        <Tooltip
                                          title={value === "—" ? "" : value}
                                        >
                                          <Box
                                            component="span"
                                            sx={{
                                              display: "block",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {value}
                                          </Box>
                                        </Tooltip>
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Box>
                  )}
                </Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ flexShrink: 0, pt: 0.5 }}
                >
                  <Button variant="outlined" onClick={() => setStep("mapping")}>
                    Back to mappings
                  </Button>
                  <Button
                    variant="contained"
                    onClick={confirmImport}
                    disabled={preview.errorCount > 0}
                  >
                    Import {preview.validCount} rows
                  </Button>
                </Stack>
              </Stack>
            )}

          {step === "confirming" && (
            <ProcessLoader
              title="Starting import"
              messages={CONFIRM_STATUS}
              caption="The import form is being created and rows are being queued. You’ll see live progress next."
              icon={
                <PlaylistAddCheckOutlined
                  color="primary"
                  sx={{ fontSize: 28 }}
                />
              }
            />
          )}

          {step === "polling" && (
            <Stack spacing={3} sx={{ py: 3 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  mx: "auto",
                  borderRadius: "50%",
                  bgcolor: "rgba(103, 80, 164, 0.08)",
                  display: "grid",
                  placeItems: "center",
                  position: "relative",
                  animation: "mappingPulse 1.8s ease-in-out infinite",
                  "@keyframes mappingPulse": {
                    "0%, 100%": { transform: "scale(1)", opacity: 0.8 },
                    "50%": { transform: "scale(1.06)", opacity: 1 },
                  },
                }}
              >
                <CircularProgress
                  size={72}
                  thickness={2.4}
                  sx={{ position: "absolute" }}
                />
                <CloudUploadOutlined color="primary" sx={{ fontSize: 28 }} />
              </Box>
              <Box textAlign="center">
                <Typography variant="h6" fontWeight={600}>
                  Import in progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {phaseLabel}
                </Typography>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    {toNumber(job?.processedRows).toLocaleString()} of{" "}
                    {toNumber(job?.totalRows).toLocaleString()} processed
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {Math.round(progress)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 8 }}
                />
              </Box>
              <Stack direction="row" spacing={2}>
                <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Successful
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {toNumber(job?.successCount).toLocaleString()}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {toNumber(job?.failedCount).toLocaleString()}
                  </Typography>
                </Paper>
              </Stack>
              <Alert severity="info">
                You can close this drawer. Progress will remain available in
                Import history.
              </Alert>
            </Stack>
          )}

          {step === "done" && (
            <Stack spacing={2.5} alignItems="stretch">
              <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
                {hasImportErrors ? (
                  <WarningAmberOutlined color="warning" sx={{ fontSize: 54 }} />
                ) : (
                  <CheckCircleOutline color="success" sx={{ fontSize: 54 }} />
                )}
                <Typography variant="h6" fontWeight={600}>
                  {hasImportErrors
                    ? "Import completed with errors"
                    : "Import complete"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {hasImportErrors
                    ? "Most rows were imported successfully. Review the failed rows below or download the full result file."
                    : "Mapped responses have been added to the participant registry."}
                </Typography>
              </Stack>

              {hasImportErrors && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberOutlined fontSize="inherit" />}
                >
                  {job?.errorSummary ||
                    `${toNumber(job?.failedCount).toLocaleString()} row(s) could not be imported.`}
                </Alert>
              )}

              <Stack direction="row" spacing={2}>
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 2,
                    textAlign: "center",
                    borderColor: hasImportErrors ? undefined : "success.light",
                    bgcolor: hasImportErrors
                      ? undefined
                      : (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(46, 125, 50, 0.08)"
                            : "rgba(46, 125, 50, 0.04)",
                  }}
                >
                  <Typography variant="h5" color="success.main">
                    {toNumber(job?.successCount).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Imported
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 2,
                    textAlign: "center",
                    ...(hasImportErrors && {
                      borderColor: "error.light",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(211, 47, 47, 0.08)"
                          : "rgba(211, 47, 47, 0.04)",
                    }),
                  }}
                >
                  <Typography
                    variant="h5"
                    color={hasImportErrors ? "error.main" : "text.secondary"}
                  >
                    {toNumber(job?.failedCount).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                </Paper>
              </Stack>

              {failedRows.length > 0 && (
                <ScreenerImportFailedRowsTable
                  rows={failedRows}
                  maxHeight={320}
                  subtitle="Fix these rows in your CSV and re-import if needed."
                />
              )}

              <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                {resultUrl && (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadOutlined />}
                    href={resultUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download result
                  </Button>
                )}
                <Button variant="contained" onClick={handleClose}>
                  Close
                </Button>
              </Stack>
            </Stack>
          )}

          {step === "failed" && (
            <Stack spacing={2.5} alignItems="center" sx={{ py: 4 }}>
              <ErrorOutline color="error" sx={{ fontSize: 54 }} />
              <Typography variant="h6" fontWeight={600}>
                Import failed
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                {job?.errorSummary ||
                  "The import could not be completed. Please try again."}
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button variant="outlined" onClick={handleClose}>
                  Close
                </Button>
                <Button variant="contained" onClick={restart}>
                  Start over
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ScreenerImportDrawer;
